import Papa from 'papaparse';
import {
  buildCustomerName,
  buildInitialDuplicateDecisions,
  findCustomerDuplicateGroups,
  parseFreshaBirthDate,
  parseFreshaGenre,
  parseFreshaPhone,
  revalidateCustomerRow,
} from './freshaCustomerCsv';
import { parseVcfCustomerFile } from './vcfCustomerFile';

export { buildInitialDuplicateDecisions, findCustomerDuplicateGroups, revalidateCustomerRow };

export const CUSTOMER_IMPORT_TARGET_FIELDS = [
  { id: 'name', required: true },
  { id: 'firstName', required: false },
  { id: 'lastName', required: false },
  { id: 'email', required: false },
  { id: 'phone', required: false },
  { id: 'birthDate', required: false },
  { id: 'genre', required: false },
];

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const normalizeWhitespace = (value) => (value == null ? '' : String(value).trim());

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((line) => line.trim()) || '');
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons >= commas ? ';' : ',';
};

const isVcfFile = (fileName, fileText) => {
  if (/\.vcf$/i.test(fileName || '')) {
    return true;
  }
  return /^\s*BEGIN:VCARD/im.test(fileText || '');
};

export const parseContactBirthDate = (rawValue) => {
  const raw = normalizeWhitespace(rawValue);
  if (!raw) {
    return { birthDate: null, warningKey: null, original: '' };
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day
    ) {
      return {
        birthDate: `${iso[1]}-${iso[2]}-${iso[3]}`,
        warningKey: null,
        original: raw,
      };
    }
    return {
      birthDate: null,
      warningKey: 'dataImport.customers.warnings.invalidBirthDate',
      original: raw,
    };
  }

  return parseFreshaBirthDate(rawValue);
};

const parseCsvCustomerFile = (fileText) => {
  const delimiter = detectDelimiter(fileText);
  const parsed = Papa.parse(fileText, {
    delimiter,
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (header) => normalizeWhitespace(header),
  });

  const sourceFields = (parsed.meta?.fields || [])
    .map((field) => normalizeWhitespace(field))
    .filter(Boolean);

  if (!parsed.data?.length || sourceFields.length === 0) {
    return {
      records: [],
      sourceFields: [],
      format: 'csv',
      errorKey: 'customersManagement.import.errors.emptyFile',
    };
  }

  const records = parsed.data
    .map((row) => {
      const values = {};
      sourceFields.forEach((field) => {
        values[field] = normalizeWhitespace(row[field]);
      });
      return values;
    })
    .filter((values) => sourceFields.some((field) => values[field]));

  if (!records.length) {
    return {
      records: [],
      sourceFields,
      format: 'csv',
      errorKey: 'customersManagement.import.errors.emptyFile',
    };
  }

  return { records, sourceFields, format: 'csv', errorKey: null };
};

export const parseCustomerImportFile = (fileText, fileName = '') => {
  if (!normalizeWhitespace(fileText)) {
    return { records: [], sourceFields: [], format: 'csv', errorKey: 'customersManagement.import.errors.emptyFile' };
  }

  if (isVcfFile(fileName, fileText)) {
    return { ...parseVcfCustomerFile(fileText), format: 'vcf' };
  }

  return parseCsvCustomerFile(fileText);
};

const FIELD_ALIASES = {
  name: ['display name', 'full name', 'fn', 'name'],
  firstName: ['first name', 'n (first)', 'given name'],
  lastName: ['last name', 'n (last)', 'family name', 'surname'],
  email: ['e-mail address', 'email address', 'email (work)', 'email (home)', 'email', 'e-mail'],
  phone: ['mobile phone', 'tel (cell)', 'cell phone', 'mobile', 'cell', 'tel'],
  birthDate: ['birthday', 'bday', 'birth date', 'date of birth'],
  genre: ['gender', 'genre', 'sex'],
};

const normalizeFieldName = (value) => normalizeWhitespace(value).toLowerCase();

const fieldMatchesAlias = (normalized, alias) => {
  if (normalized === alias) {
    return true;
  }

  // "TEL (CELL) 2" should match "tel (cell)", but "tel" must not steal "TEL (HOME)".
  if (alias.includes(' ') || alias.includes('(')) {
    return normalized.startsWith(`${alias} `);
  }

  return false;
};

export const suggestCustomerImportMappings = (sourceFields) => {
  const mappings = [];
  const usedSources = new Set();
  const usedTargets = new Set();

  CUSTOMER_IMPORT_TARGET_FIELDS.forEach((target) => {
    const aliases = FIELD_ALIASES[target.id] || [];
    const match = sourceFields.find((field) => {
      if (usedSources.has(field)) return false;
      const normalized = normalizeFieldName(field);
      return aliases.some((alias) => fieldMatchesAlias(normalized, alias));
    });
    if (!match || usedTargets.has(target.id)) {
      return;
    }
    usedSources.add(match);
    usedTargets.add(target.id);
    mappings.push({ sourceField: match, targetField: target.id });
  });

  return mappings;
};

export const canContinueCustomerImportMapping = (mappings) => {
  const mapped = new Set((mappings || []).map((mapping) => mapping.targetField));
  return mapped.has('name') || mapped.has('firstName');
};

const valueForTarget = (record, mappings, targetField) => {
  const mapping = mappings.find((item) => item.targetField === targetField);
  if (!mapping) {
    return '';
  }
  return normalizeWhitespace(record[mapping.sourceField]);
};

const pickRecordValue = (record, labels) => {
  const keys = Object.keys(record || {});
  for (const label of labels) {
    const exact = keys.find((key) => key === label && record[key]);
    if (exact) {
      return normalizeWhitespace(record[exact]);
    }

    const numbered = keys.find((key) => key.startsWith(`${label} `) && record[key]);
    if (numbered) {
      return normalizeWhitespace(record[numbered]);
    }
  }

  return '';
};

const buildCustomerImportRow = (index, record, fields) => {
  const {
    fullName,
    firstName,
    lastName,
    email,
    phoneRaw,
    genreRaw,
    birthDateRaw,
  } = fields;

  const name = buildCustomerName(firstName, lastName, fullName);
  const phoneParse = parseFreshaPhone(phoneRaw);
  const genreParse = parseFreshaGenre(genreRaw);
  const birthDateParse = parseContactBirthDate(birthDateRaw);

  const errors = [];
  if (!name) {
    errors.push({
      field: 'name',
      key: 'dataImport.customers.errors.nameRequired',
      original: fullName || firstName,
    });
  }
  const hasPhone = Boolean(phoneParse.phone?.countryCode && phoneParse.phone?.number);
  if (email) {
    if (!EMAIL_REGEX.test(email)) {
      errors.push({
        field: 'email',
        key: 'dataImport.customers.errors.emailInvalid',
        original: email,
      });
    }
  } else if (!hasPhone) {
    errors.push({
      field: 'contact',
      key: 'dataImport.customers.errors.contactRequired',
      original: phoneRaw || email,
    });
  }

  const warnings = [];
  if (phoneParse.warningKey) {
    warnings.push({ field: 'phone', key: phoneParse.warningKey, original: phoneParse.original });
  }
  if (genreParse.warningKey) {
    warnings.push({ field: 'genre', key: genreParse.warningKey, original: genreParse.original });
  }
  if (birthDateParse.warningKey) {
    warnings.push({ field: 'birthDate', key: birthDateParse.warningKey, original: birthDateParse.original });
  }

  const rawCells = Object.entries(record).map(([header, value]) => ({
    header,
    value: normalizeWhitespace(value),
  }));

  return {
    id: `import-${index + 1}`,
    rowNumber: index + 1,
    name,
    email,
    phone: phoneParse.phone,
    genre: genreParse.genre,
    birthDate: birthDateParse.birthDate,
    rawCells,
    errors,
    warnings,
    original: {
      name: fullName || [firstName, lastName].filter(Boolean).join(' '),
      email,
      phone: phoneRaw,
      genre: genreRaw,
      birthDate: birthDateRaw,
    },
  };
};

export const applyCustomerImportMapping = (records, mappings) => {
  return (records || []).map((record, index) => buildCustomerImportRow(index, record, {
    fullName: valueForTarget(record, mappings, 'name'),
    firstName: valueForTarget(record, mappings, 'firstName'),
    lastName: valueForTarget(record, mappings, 'lastName'),
    email: valueForTarget(record, mappings, 'email'),
    phoneRaw: valueForTarget(record, mappings, 'phone'),
    genreRaw: valueForTarget(record, mappings, 'genre'),
    birthDateRaw: valueForTarget(record, mappings, 'birthDate'),
  }));
};

export const applyVcfCustomerRecords = (records) => {
  return (records || []).map((record, index) => buildCustomerImportRow(index, record, {
    fullName: normalizeWhitespace(record.FN),
    firstName: normalizeWhitespace(record['N (First)']),
    lastName: normalizeWhitespace(record['N (Last)']),
    email: pickRecordValue(record, ['EMAIL (WORK)', 'EMAIL (HOME)', 'EMAIL']),
    phoneRaw: pickRecordValue(record, ['TEL (CELL)', 'TEL (MOBILE)', 'TEL']),
    genreRaw: normalizeWhitespace(record.GENDER || record.SEX),
    birthDateRaw: normalizeWhitespace(record.BDAY),
  }));
};

export const buildStoreCustomersImportPayload = (rows, mergeDecisions) => {
  const readyRows = rows.filter((row) => row.errors.length === 0);
  const rowById = new Map(readyRows.map((row) => [row.id, row]));
  const consumed = new Set();
  const customers = [];

  Object.entries(mergeDecisions || {}).forEach(([, decision]) => {
    if (decision?.action !== 'merge' || !decision.primaryRowId) return;
    const primary = rowById.get(decision.primaryRowId);
    if (!primary) return;

    const memberIds = decision.memberIds || [];
    memberIds.forEach((id) => consumed.add(id));
    customers.push({
      name: primary.name,
      email: primary.email || null,
      phone: primary.phone || null,
      genre: primary.genre || null,
      birthDate: primary.birthDate || null,
    });
  });

  readyRows.forEach((row) => {
    if (consumed.has(row.id)) return;
    customers.push({
      name: row.name,
      email: row.email || null,
      phone: row.phone || null,
      genre: row.genre || null,
      birthDate: row.birthDate || null,
    });
  });

  return { customers };
};
